import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  Button,
  DebouncedTextField,
  Flex,
  IconButton,
  IconMenu,
  MagnifyingGlassIcon,
  SparklesIcon,
  TableCellsIcon,
  Text,
  Tooltip,
} from 'components'
import { useAlert } from 'components/providers'
import { startCase } from 'lodash-es'
import { useState } from 'react'
import { updateEventSmartValues } from 'utils'
import { LeadTableColumn } from '../../../utils'
import { useEvent } from '../../../utils/eventUtils'

interface Props {
  columns: LeadTableColumn[]
  onChange: (columns: LeadTableColumn[]) => void
  onReset?: () => void
}

export default function LeadTableColumnSelector({ columns, onChange, onReset }: Props) {
  const { confirm } = useAlert()
  const { event } = useEvent()
  const [search, setSearch] = useState('')

  async function updateSmartValues() {
    const confirmed = await confirm(
      `This will update all the sessions recorded for this event with data from the latest smart columns. This action cannot be undone. Do you want to continue?`
    )
    if (confirmed && event) updateEventSmartValues({ eventId: event.id })
  }

  function selectAllColumns() {
    const allSelected = columns.every(c => c.visible)
    onChange(columns.map(c => ({ ...c, visible: allSelected ? false : true })))
  }

  function handleClose() {
    if (!columns.length) onReset?.()
    setSearch('')
  }

  const smartColumnUpdateStatus = event?.smartColumns?.updateStatus
  const filteredColumns = columns.filter(c => {
    if (c.alwaysSelected) return false
    return search ? c.key.toLowerCase().includes(search.toLowerCase()) : true
  })
  const smartColumns = filteredColumns.filter(c => c.source === 'smart')
  const otherColumns = filteredColumns.filter(c => c.source !== 'smart')
  const selectedColumns = columns.filter(c => c.visible)

  const smartColumnsRenderOptions = smartColumns.length
    ? [
        {
          type: 'custom' as const,
          label: (
            <Flex px={4} align="center" justify="space-between">
              <Flex gap={4} align="center">
                <SparklesIcon color="primary.500" />
                <Text textStyle="bodySmall">Smart Columns</Text>
              </Flex>
              {(smartColumnUpdateStatus === 'needs-update' ||
                smartColumnUpdateStatus === 'updating') && (
                <Tooltip
                  content={
                    smartColumnUpdateStatus === 'needs-update'
                      ? 'Data for smart columns in this event has been updated. Recalculate values for this event to match the latest data.'
                      : 'Updating smart column values'
                  }
                >
                  <IconButton
                    icon={<ArrowPathIcon fontSize="md" color="green.500" />}
                    aria-label={
                      smartColumnUpdateStatus === 'needs-update'
                        ? 'update smart column values'
                        : 'updating smart column values'
                    }
                    onClick={
                      smartColumnUpdateStatus === 'needs-update' ? updateSmartValues : undefined
                    }
                    loading={smartColumnUpdateStatus === 'updating'}
                  />
                </Tooltip>
              )}
            </Flex>
          ),
        },
        {
          type: 'group' as const,
          options: smartColumns.map(({ key }) => ({
            value: key,
            label: (
              <Flex gap={3} pl={2}>
                <Text textStyle={'bodySmall'} fontWeight={'normal'}>
                  {startCase(key)}
                </Text>
              </Flex>
            ),
          })),
          py: 2,
          maxHeight: '200px',
          overflow: 'auto',
        },
        { type: 'divider' as const, m: 0 },
      ]
    : []

  return (
    <IconMenu
      icon={<TableCellsIcon />}
      menuProps={{
        borderRadius: 'sm',
        colorMode: 'light',
        contentProps: { overflowX: 'hidden', p: 0, width: '370px' },
        disabled: columns.length === 0,
        gap: { base: 0, sm: 2 },
        isMulti: true,
        menuProps: {
          closeOnSelect: false,
          onOpenChange: p => !p.open && handleClose(),
          positioning: { placement: 'bottom-end' },
        },
        onChange: v => onChange(columns.map(c => ({ ...c, visible: v.includes(c.key) }))),
        options: [
          {
            label: (
              <Flex borderTopRadius="sm" width="100%" direction="column" gap={4} px={5} pt={5}>
                <Text textStyle="bodyXSmall" fontWeight="bold" opacity={0.6}>
                  View Columns
                </Text>
                <DebouncedTextField
                  width="100%"
                  boxShadow="0px 1px 4px 0px rgba(0, 0, 0, 0.06)"
                  controlled
                  disabled={columns.length === 0}
                  leftElement={<MagnifyingGlassIcon color="rgba(23, 23, 23, 0.6)" />}
                  onChange={setSearch}
                  placeholder="Search..."
                  pl={10}
                  value={search}
                  backdropFilter="blur(14px)"
                  borderRadius="10px"
                  borderWidth={0}
                />
                <Flex align="center" justify="space-between">
                  <Flex align="center" gap={2} cursor="pointer" onClick={selectAllColumns}>
                    {columns.length > 0 ? (
                      <Text textStyle='bodySmall'>
                        {selectedColumns.length} of {columns.length} Columns Selected
                      </Text>
                    ) : (
                      <Text textStyle={'bodySmall'}>{columns.length} Total Columns</Text>
                    )}
                  </Flex>
                  <Button
                    disabled={!onReset}
                    leftIcon={<ArrowUturnLeftIcon fontSize="sm" />}
                    size="sm"
                    variant="ghost"
                    onClick={() => onReset?.()}
                  >
                    <Text textStyle={'bodySmall'} textAlign={'center'}>
                      Reset
                    </Text>
                  </Button>
                </Flex>
              </Flex>
            ),
            type: 'custom',
          },
          { type: 'divider', m: 0 },
          ...smartColumnsRenderOptions,
          {
            options: otherColumns.map(({ key, title }) => ({
              gap: 4,
              label: startCase(title ?? key),
              value: key,
            })),
            py: 2,
            maxHeight: '200px',
            type: 'group',
          },
        ],
        portal: true,
        value: columns.filter(c => c.visible).map(c => c.key),
        variant: 'solid',
      }}
      tooltipContent="Columns"
    />
  )
}
